import { Schema } from "effect";
import {
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from "react";

import { Sheet, SheetPopup, SheetTitle } from "~/components/ui/sheet";
import { useLocalStorage } from "~/hooks/useLocalStorage";
import { useIsMobile } from "~/hooks/useMediaQuery";

export const ASSISTANT_PANEL_WIDTH = { default: 400, min: 320, max: 720, step: 16 } as const;

/** Keeps the panel between its minimum and at most 60% of the window. */
export function clampAssistantPanelWidth(width: number, viewportWidth: number): number {
  const max = Math.max(
    ASSISTANT_PANEL_WIDTH.min,
    Math.min(ASSISTANT_PANEL_WIDTH.max, Math.floor(viewportWidth * 0.6)),
  );
  return Math.round(Math.min(max, Math.max(ASSISTANT_PANEL_WIDTH.min, width)));
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT"
  );
}

/**
 * `/` opens the assistant and focuses its composer, unless the person is
 * already typing somewhere.
 */
export function useAssistantComposerShortcut(input: {
  composerId: string;
  enabled: boolean;
  onOpen: () => void;
}) {
  const { composerId, enabled, onOpen } = input;
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.defaultPrevented || isTyping(event.target)) return;
      event.preventDefault();
      onOpen();
      // The panel or sheet may mount on this open; focus once it has.
      const focus = (attempts: number) => {
        const composer = document.getElementById(composerId);
        if (composer) composer.focus();
        else if (attempts > 0) requestAnimationFrame(() => focus(attempts - 1));
      };
      requestAnimationFrame(() => focus(10));
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [composerId, enabled, onOpen]);
}

/**
 * The dashboard with its assistant beside it: a resizable, collapsible panel
 * on the right on wide screens, and a bottom sheet on a phone. `Esc` inside
 * the panel closes it and returns focus to `returnFocusId` (the Ask button).
 */
export function DashboardAssistantLayout(props: {
  children: ReactNode;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  /** The panel, given the close action (which returns focus to the Ask button). */
  panel: (close: () => void) => ReactNode;
  /** Shown to screen readers as the sheet's name on a phone. */
  panelTitle: string;
  returnFocusId: string;
}) {
  const { onOpenChange, returnFocusId } = props;
  const isMobile = useIsMobile();
  const [storedWidth, setStoredWidth] = useLocalStorage(
    "dashboards:assistant-width",
    ASSISTANT_PANEL_WIDTH.default,
    Schema.Finite,
  );
  const width = clampAssistantPanelWidth(storedWidth, window.innerWidth);
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);

  const close = useCallback(() => {
    onOpenChange(false);
    requestAnimationFrame(() => document.getElementById(returnFocusId)?.focus());
  }, [onOpenChange, returnFocusId]);

  const resizeTo = (next: number) =>
    setStoredWidth(clampAssistantPanelWidth(next, window.innerWidth));

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { startX: event.clientX, startWidth: width };
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    resizeTo(drag.current.startWidth + drag.current.startX - event.clientX);
  };
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };
  const onSeparatorKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = ASSISTANT_PANEL_WIDTH.step;
    if (event.key === "ArrowLeft") resizeTo(width + step);
    else if (event.key === "ArrowRight") resizeTo(width - step);
    else if (event.key === "Home") resizeTo(ASSISTANT_PANEL_WIDTH.max);
    else if (event.key === "End") resizeTo(ASSISTANT_PANEL_WIDTH.min);
    else return;
    event.preventDefault();
  };

  if (isMobile) {
    return (
      <>
        {props.children}
        <Sheet open={props.open} onOpenChange={onOpenChange}>
          <SheetPopup
            className="h-[85svh] rounded-t-2xl p-0"
            data-assistant-sheet=""
            showCloseButton={false}
            side="bottom"
          >
            <SheetTitle className="sr-only">{props.panelTitle}</SheetTitle>
            {props.panel(close)}
          </SheetPopup>
        </Sheet>
      </>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full min-w-0" data-assistant-layout="">
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">{props.children}</div>
      {props.open ? (
        <>
          <div
            aria-label="Resize assistant"
            aria-orientation="vertical"
            aria-valuemax={ASSISTANT_PANEL_WIDTH.max}
            aria-valuemin={ASSISTANT_PANEL_WIDTH.min}
            aria-valuenow={width}
            className="w-1 shrink-0 cursor-col-resize touch-none bg-border transition-colors outline-none hover:bg-ring/48 focus-visible:bg-ring"
            onKeyDown={onSeparatorKeyDown}
            onPointerCancel={onPointerUp}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            role="separator"
            tabIndex={0}
          />
          <aside
            className="flex h-full min-h-0 shrink-0 flex-col"
            data-assistant-panel=""
            onKeyDown={(event) => {
              if (event.key !== "Escape" || event.defaultPrevented) return;
              event.preventDefault();
              close();
            }}
            style={{ width }}
          >
            {props.panel(close)}
          </aside>
        </>
      ) : null}
    </div>
  );
}
